import cloneDeep from 'clone-deep'
import { IVisibleLogic, IVisibleProp, NewInputTextOptions, NewInputTypeOptions } from 'config'
import { useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { InputVisibleLogic, Modal } from 'stories/components'
import { ISelectOption } from 'stories/components/SelectOptionInputs/types'
import { getPartiesMap } from 'utils'
import { RenderInput } from 'utils/RenderInput'

export const FieldPropertyModal = ({
  fieldKey,
  field,
  allFields = {},
  onClose,
}: {
  fieldKey: string
  field: IVisibleProp
  allFields?: Record<string, IVisibleProp>
  onClose: (key: string, updated: IVisibleProp | null) => void
}) => {
  const [title, setTitle] = useState(field.title || '')
  const [cursorPos, setCursorPos] = useState(0)
  const activeInputRef = useRef<HTMLInputElement | null>(null)

  const handleTitleFocus = (event: any) => {
    activeInputRef.current = event.target
    setCursorPos(event.target.selectionStart ?? 0)
  }

  const handleCursorChange = (event: any) => {
    setCursorPos(event.target.selectionStart ?? 0)
  }

  const insertPlaceholder = (key: string) => {
    const text = `{{${key}}}`
    const input = activeInputRef.current
    if (!input) {
      setTitle((prev) => prev + text)
      return
    }
    const value = input.value
    const newValue = value.substring(0, cursorPos) + text + value.substring(cursorPos)
    const newCursorPos = cursorPos + text.length
    setTitle(newValue)
    setTimeout(() => {
      input.focus({ preventScroll: true })
      input.setSelectionRange(newCursorPos, newCursorPos)
      setCursorPos(newCursorPos)
    }, 0)
  }

  const [inputType, setInputType] = useState(() => {
    const raw = field.inputType || 'Text'
    return Object.keys(NewInputTypeOptions).find((k) => k.toLowerCase() === raw.toLowerCase()) || raw
  })
  const [textType, setTextType] = useState(() => {
    const raw = field.type || 'text'
    return Object.keys(NewInputTextOptions).find((k) => k.toLowerCase() === raw.toLowerCase()) || raw
  })
  const [inParentSection, setInParentSection] = useState<boolean>((field as any).inParentSection || false)
  const [visibleLogic, setVisibleLogic] = useState<IVisibleLogic[][]>(cloneDeep(field.visibleLogic || []))
  const [selectOptions, setSelectOptions] = useState<ISelectOption[]>(() => {
    if (field.options && typeof field.options === 'object') {
      if (Array.isArray(field.options)) return field.options as ISelectOption[]
      return Object.entries(field.options as any as Record<string, string>).map(([key, title]) => ({
        key,
        title,
        visible: true,
        isCustom: true,
      }))
    }
    return [
      { key: 'Yes', title: 'Yes', visible: true, isCustom: true },
      { key: 'No', title: 'No', visible: true, isCustom: true },
    ]
  })

  const otherFieldOptions: Record<string, string> = {}
  Object.entries(allFields).forEach(([k, f]) => {
    if (k !== fieldKey) otherFieldOptions[k] = f.title || k
  })

  const onSubmit = () => {
    if (!title.trim()) {
      toast('Title is required', { type: 'warning' })
      return
    }
    const updated: IVisibleProp = { ...field, title: title.trim(), inputType }
    if (inputType.toLowerCase() === 'text') updated.type = textType
    else delete updated.type
    if (inputType.toLowerCase() === 'select') {
      const visibleOptions = selectOptions.filter((o) => o.visible !== false && o.title.trim())
      if (visibleOptions.length === 0) {
        toast('At least one option is required', { type: 'warning' })
        return
      }
      updated.options = selectOptions
      updated.hasDefaultOption = true
    } else {
      delete updated.options
      delete updated.hasDefaultOption
    }
    const cleanLogic = visibleLogic.filter((g) => g.some((c) => c.fieldKey))
    if (cleanLogic.length > 0) updated.visibleLogic = cleanLogic
    else delete updated.visibleLogic
    ;(updated as any).inParentSection = inParentSection
    onClose(fieldKey, updated)
  }

  return (
    <Modal
      isOpen
      title={fieldKey ? 'Edit Field' : 'New Field'}
      titleOkay="Submit"
      onClose={() => onClose(fieldKey, null)}
      onOk={onSubmit}
    >
      <div className="w-144 flex flex-col gap-4">
        <RenderInput
          Key="title"
          input={{ inputType: 'text', title: 'Title', value: title }}
          onChange={(_k: string, v: any) => setTitle(v as string)}
          onFocus={(e: any) => handleTitleFocus(e)}
          onKeyUp={handleCursorChange}
          onClick={handleCursorChange}
        />
        <div>
          <div className="border-b mb-2 text-sm font-medium text-gray-600">
            - Placeholders (click to insert into title)
          </div>
          <pre className="flex gap-1 flex-wrap text-xs">
            {Object.keys(getPartiesMap({}))
              .sort()
              .map((key, index) => (
                <span
                  key={index}
                  className="mr-2 cursor-pointer hover:underline"
                  onClick={() => insertPlaceholder(key)}
                >
                  {`{{${key}}}`}
                </span>
              ))}
          </pre>
        </div>

        <RenderInput
          Key="inParentSection"
          input={{ inputType: 'checkbox', title: 'In Parent Section', value: inParentSection }}
          onChange={(_k: string, v: any) => setInParentSection(v as boolean)}
        />
        <RenderInput
          Key="inputType"
          input={{
            inputType: 'select',
            title: 'Input Type',
            value: inputType,
            options: NewInputTypeOptions,
            hasDefaultOption: true,
          }}
          onChange={(_k: string, v: any) => setInputType(v as string)}
        />

        {inputType.toLowerCase() === 'select' && (
          <RenderInput
            Key="selectOptions"
            input={{
              inputType: 'SelectOptionInput',
              title: 'Select Options',
              value: selectOptions,
              required: true,
            }}
            onChange={(_k: string, v: any) => setSelectOptions(v)}
          />
        )}

        {inputType.toLowerCase() === 'text' && (
          <RenderInput
            Key="textType"
            input={{
              inputType: 'select',
              title: 'Text Format',
              value: textType,
              options: NewInputTextOptions,
              hasDefaultOption: true,
            }}
            onChange={(_k: string, v: any) => setTextType(v as string)}
          />
        )}

        <InputVisibleLogic
          id="visible-logic"
          value={visibleLogic}
          onChange={setVisibleLogic}
          options={{
            additionalFieldOrigins: {
              CollateralReview: {
                title: 'Collateral Review',
                inputs: allFields as any,
              },
            },
            hasNotVisible: true,
          }}
        />
      </div>
    </Modal>
  )
}
